// DashboardHome.jsx
import React, { useState, useEffect } from 'react';
import { 
    DollarSign,
    Megaphone,
    Gavel,
    Activity
} from 'lucide-react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import { formatCurrency, formatDateIntl, formatPercentage } from "../../util/currency_lookup";
import { Loader } from '../../components/loader';
import './DashboardHome.css';
import CurrencySelector from '../../components/CurrencySelector';

const FundManagerHome = () => {
    const [loading, setLoading] = useState({
        stats: true,
        trends: true,
    });
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [selectedCurrency, setSelectedCurrency] = useState('GBP');
    const [trends, setTrends] = useState([]);
    const [timeframe, setTimeframe] = useState('month'); // week, month, year

    useEffect(() => {
        fetchDashboardData(selectedCurrency);
        fetchInvestmentGrowth(selectedCurrency);
    }, [timeframe, selectedCurrency]);


    const fetchInvestmentGrowth = async (targetCurrency) => {
        try {
            const token = localStorage.getItem('token');
            const baseUrl = process.env.REACT_APP_API_URL;

            const trendsResponse = await fetch(
                `${baseUrl}/api/v1/analytics/${targetCurrency}/investment-growth`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (!trendsResponse.ok) {
                setError("error loading data");
                setLoading(prev => ({ ...prev, trends: false }));
                return;
            }
            const trendsData = await trendsResponse.json();
            setTrends(trendsData);
            setLoading(prev => ({ ...prev, trends: false }));
        } catch(err) {
            setError("error loading data");
            setLoading(prev => ({ ...prev, trends: false }));
        }
    };

    const fetchDashboardData = async (targetCurrency) => {
        try {
            const token = localStorage.getItem('token');
            const baseUrl = process.env.REACT_APP_API_URL;
            
            // Fetch basic stats
            const statsResponse = await fetch(`${baseUrl}/api/v1/analytics/${targetCurrency}/fm-top-line`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!statsResponse.ok) {
                setError("error loading data");
                setLoading(prev => ({ ...prev, stats: false }));
                return;
            }
            const statsData = await statsResponse.json();
            setStats(statsData);
            setLoading(prev => ({ ...prev, stats: false }));
        } catch (err) {
            setError(err.message);
            setLoading({
                stats: false
            });
        }
    };

    const isLoading = Object.values(loading).some(status => status);

    if (isLoading) {
        return <Loader loading={true} />;
    }

    if (error) {
        return <div className="error">{error}</div>;
    }

    const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444'];

    const handleCurrencyChange = (currency) => {
        setSelectedCurrency(currency);
    };

    return (
            <div className="dashboard-home">
            {/* Stats Grid */}

            <div className="dashboard-header">
                <h1>Dashboard</h1>
                <CurrencySelector 
                    selectedCurrency={selectedCurrency}
                    onCurrencyChange={handleCurrencyChange}
                />
            </div>

            <div className="dashboard-stats-grid">
                <div className="dashboard-stat-card">
                    <div className="stat-icon">
                        <DollarSign />
                    </div>
                    <div className="stat-content">
                        <div className="dashboard-stat-title">Total Investments</div>
                        <div className="dashboard-stat-value">
                            {formatCurrency(stats.aum, selectedCurrency)}
                        </div>
                        {/* <div className={`stat-change ${stats.aum >= 0 ? 'positive' : 'negative'}`}>
                            {stats.investment_change >= 0 ? <ArrowUpRight /> : <ArrowDownRight />}
                            {formatPercentage(Math.abs(stats.investment_change))}
                        </div> */}
                    </div>
                </div>

                <div className="dashboard-stat-card">
                    <div className="stat-icon">
                        <Gavel />
                    </div>
                    <div className="stat-content">
                        <div className="dashboard-stat-title">Buy Volume</div>
                        <div className="dashboard-stat-value">{formatCurrency(stats.buy_volume, selectedCurrency)}</div>
                        {/* <div className={`stat-change ${stats.user_change >= 0 ? 'positive' : 'negative'}`}>
                            {stats.user_change >= 0 ? <ArrowUpRight /> : <ArrowDownRight />}
                            {formatPercentage(Math.abs(stats.user_change))}
                        </div> */}
                    </div>
                </div>

                <div className="dashboard-stat-card">
                    <div className="stat-icon">
                        <Megaphone />
                    </div>
                    <div className="stat-content">
                        <div className="dashboard-stat-title">Sold Volume</div>
                        <div className="dashboard-stat-value">{formatCurrency(stats.sell_volume, selectedCurrency)}</div>
                        {/* <div className={`stat-change ${stats.fund_change >= 0 ? 'positive' : 'negative'}`}>
                            {stats.fund_change >= 0 ? <ArrowUpRight /> : <ArrowDownRight />}
                            {formatPercentage(Math.abs(stats.fund_change))}
                        </div> */}
                    </div>
                </div>

                <div className="dashboard-stat-card">
                    <div className="stat-icon">
                        <Activity />
                    </div>
                    <div className="stat-content">
                        <div className="dashboard-stat-title">Avg Volume</div>
                        <div className="dashboard-stat-value">
                            {formatCurrency(stats.avg_sell_size, selectedCurrency)}
                        </div>
                        {/* <div className={`stat-change ${stats.volume_change >= 0 ? 'positive' : 'negative'}`}>
                            {stats.volume_change >= 0 ? <ArrowUpRight /> : <ArrowDownRight />}
                            {formatPercentage(Math.abs(stats.volume_change))}
                        </div> */}
                    </div>
                </div>
            </div>


            {/* Charts Grid */}
            <div className="charts-grid">
                {/* Investment Trends */}
                <div className="chart-card large">
                    <div className="chart-header">
                        <h3>Investment Trends</h3>
                        <select 
                            value={timeframe} 
                            onChange={(e) => setTimeframe(e.target.value)}
                            className="timeframe-select"
                        >
                            <option value="week">This Week</option>
                            <option value="month">This Month</option>
                            <option value="year">This Year</option>
                        </select>
                    </div>
                    <div className="chart-content">
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={trends.metrics}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="date" 
                                    tickFormatter={(date) => formatDateIntl(date, 'short')}
                                />
                                <YAxis 
                                    tickFormatter={(value) => formatCurrency(value, selectedCurrency, true)}
                                />
                                <Tooltip 
                                    formatter={(value) => formatCurrency(value, selectedCurrency)}
                                    labelFormatter={(date) => formatDateIntl(date)}
                                />
                                <Area 
                                    type="monotone" 
                                    dataKey="daily_transactions" 
                                    stroke="#6366f1" 
                                    fill="#6366f1" 
                                    fillOpacity={0.1}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

            </div>

        </div>
        
    );
};

export default FundManagerHome;
